import { PropertiesFilterFragment as PropertiesFilter } from 'buying/graphql/fragments/__generated__/PropertiesFilterFragment';

const ONE_MILLION = 1_000_000;
const MAX_PRICE = 999_999_999;

const generatePriceText = (price: number): string => {
  price = Math.round(price);

  if (price < 1000) {
    return `$${price}`;
  }

  const priceInThousands = Math.round(price / 1000);
  if (priceInThousands < 1000) {
    return `$${priceInThousands}k`;
  }

  const priceInMillions = price / ONE_MILLION;
  return `$${Math.round(priceInMillions * 10) / 10}M`;
};

const generateZoneText = (zoneNames: Array<string>): string => {
  switch (zoneNames.length) {
    case 1:
      return ` in ${zoneNames[0]}`;

    case 2:
      return ` in ${zoneNames[0]}, ${zoneNames[1]}`;

    case 3:
      return ` in ${zoneNames[0]}, ${zoneNames[1]}, +1 area`;

    default:
      return ` in ${zoneNames[0]}, ${zoneNames[1]}, +${zoneNames.length - 2} areas`;
  }
};

const generateZoneTextV2 = (zoneNames: Array<string>): string => {
  switch (zoneNames.length) {
    case 1:
      return `${zoneNames[0]}`;

    case 2:
      return `${zoneNames[0]}, ${zoneNames[1]}`;

    case 3:
      return `${zoneNames[0]}, ${zoneNames[1]}, +1 area`;

    default:
      return `${zoneNames[0]}, ${zoneNames[1]}, +${zoneNames.length - 2} areas `;
  }
};

/** OLA - Returns an array of values for price selection up to 10M */
const priceListValues = (): Array<number> => {
  let i;
  const values: number[] = [];

  for (i = 1; i <= 56; i += 1) {
    if (i * 50000 <= 1000000) {
      values.push(i * 50000);
    } else {
      values.push(values[i - 2] + 250000);
    }
  }

  return values;
};

/** OLA - Returns the next closest valid selection value for a number returned from priceListValues. Used to match the closest min/max price values from feed search, since the price selection values vary between the Feed tab feed filters and the Search tab map feed filters */
const mapToClosest = (num: number) => {
  return priceListValues().reduce((prev, curr) => {
    return Math.abs(prev - num) < Math.abs(curr - num) ? prev : curr;
  });
};

/** Returns search path url and persists feed filters when navigating from FeedFiltersSearchBar to Search tab */
export const generateSearchParams = (
  market: string | null,
  propertyFilters: PropertiesFilter | null,
): string | null => {
  if (!market) {
    return null;
  }

  let path = `/homes/${market}?`;

  if (propertyFilters) {
    const { minBedrooms, minBathrooms, minPrice, maxPrice } = propertyFilters;

    if (minBedrooms) {
      path += `bedrooms=${minBedrooms}&`;
    }

    if (minBathrooms) {
      path += `bathrooms=${minBathrooms}&`;
    }

    if (minPrice && minPrice !== 0) {
      path += `min_price=${mapToClosest(minPrice)}&`;
    }

    if (maxPrice) {
      path += `max_price=${mapToClosest(maxPrice)}`;
    }
  }

  return path;
};

/** Returns summary string for feed search in V1 */
export const generateSummary = (filters: PropertiesFilter): string => {
  let filterText = '';

  if (filters.minBedrooms) {
    filterText += `${filters.minBedrooms} br,`;
  }

  if (filters.minBathrooms) {
    filterText += ` ${filters.minBathrooms} ba,`;
  }

  if (filters.maxPrice && filters.maxPrice !== MAX_PRICE) {
    filterText += ` <${generatePriceText(filters.maxPrice)},`;
  }

  if (filters.zones && filters.zones.length > 0) {
    const zoneNames = filters.zones
      .map((zone) => zone?.geoData?.name)
      .filter((zoneName): zoneName is string => !!zoneName);

    filterText += generateZoneText(zoneNames);
  }

  return filterText.replace(/,$/, '').trim();
};

/** Returns summary string for feed filter search in V2 */
export const generateSummaryV2 = (filters: PropertiesFilter): string => {
  let filterText = '';
  const hasMinPrice = filters.minPrice && filters.minPrice !== 0;
  const hasMaxPrice = filters.maxPrice && filters.maxPrice !== MAX_PRICE;

  if (filters.zones && filters.zones.length > 0) {
    const zoneNames = filters.zones
      .map((zone) => zone?.geoData?.name)
      .filter((zoneName): zoneName is string => !!zoneName);

    filterText += `${generateZoneTextV2(zoneNames)}`;
  }

  if (hasMaxPrice || hasMinPrice) {
    filterText += ` • `;
  }

  if (hasMinPrice && filters.minPrice) {
    filterText += `${generatePriceText(filters.minPrice)}${hasMaxPrice ? ` - ` : '+'}`;
  }

  if (hasMaxPrice && filters.maxPrice) {
    filterText += `${hasMinPrice ? '' : '<'}${generatePriceText(filters.maxPrice)}`;
  }

  if (filters.minBedrooms || filters.minBathrooms) {
    filterText += ` • `;
  }

  if (filters.minBedrooms) {
    filterText += `${filters.minBedrooms} beds${
      filters.minBedrooms && filters.minBathrooms ? `, ` : ''
    }`;
  }

  if (filters.minBathrooms) {
    filterText += `${filters.minBathrooms} baths`;
  }

  return filterText.replace(/,$/, '').trim();
};
